import React from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Box, TextField, IconButton, InputAdornment } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

dayjs.extend(customParseFormat);

function toDayjsOrNull(str) {
    if (!str) return null;
    const d = dayjs(str, "DD/MM/YYYY", true);
    return d.isValid() ? d : null;
}

export default function DateFieldWithIcon({
    value,
    onChange,
    minDate,
    placeholder = "DD/MM/YYYY",
    error = false,
    helperText = "",
    onBlur,
}) {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(value || "");
    const anchorRef = React.useRef(null);

    React.useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    const pickerValue = React.useMemo(() => toDayjsOrNull(value), [value]);
    const minDayjs = React.useMemo(() => toDayjsOrNull(minDate), [minDate]);

    return (
        <Box ref={anchorRef} sx={{ width: "100%" }}>
            <DatePicker
                disableOpenPicker
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={pickerValue}
                minDate={minDayjs || undefined}
                format="DD/MM/YYYY"
                openOnFocus={false}
                onChange={(newVal) => {
                    const formatted = newVal ? dayjs(newVal).format("DD/MM/YYYY") : "";
                    setInputValue(formatted);
                    onChange(formatted);
                }}
                slotProps={{
                    popper: {
                        anchorEl: anchorRef.current,
                        disablePortal: false,
                        placement: "bottom-start",
                        sx: {
                            zIndex: (theme) => theme.zIndex.modal + 1,
                        },
                    },
                }}
                slots={{
                    textField: (params) => (
                        <TextField
                            {...params}
                            ref={params.InputProps?.ref}
                            inputRef={params.inputRef}
                            fullWidth
                            placeholder={placeholder}
                            value={inputValue}
                            onChange={(e) => {
                                const next = e.target.value;
                                setInputValue(next);
                                onChange(next);
                            }}
                            onBlur={(e) => {
                                onBlur?.(e);
                                params?.inputProps?.onBlur?.(e);
                            }}
                            error={Boolean(error)}
                            helperText={helperText}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => setOpen(true)}
                                                tabIndex={-1}
                                            >
                                                <CalendarMonthOutlinedIcon />
                                            </IconButton>
                                        </InputAdornment>

                                        {/* keep any other adornments (like clear) */}
                                        {params.InputProps?.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    ),
                }}
            />
        </Box>
    );
}
